import { zodResolver } from '@hookform/resolvers/zod'
import { Check, CornerUpLeft, Send, Undo2 } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { useToastStack } from '@/components/feedback/toast-stack'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Timeline, TimelineGroup, TimelineItem, type TimelineTone } from '@/components/ui/timeline'
import { useInvalidateCash } from '@/features/cash/hooks'
import {
  sendBackBody,
  sendBackSchema,
  type SendBackValues,
} from '@/features/cash/payment-decision-form'
import { formatDateTime } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { messages } from '@/lib/messages'
import { cashApi, type CashPaymentList } from '@/services/http/cash'

const t = messages.cash.paymentList
const FORM_ID = 'payment-send-back-form'
const NOTE_ID = 'payment-send-back-note'

type Decision = NonNullable<CashPaymentList['decisions']>[number]

const DECISION_LOOK: Record<Decision['decision'], { icon: typeof Send; tone: TimelineTone }> = {
  onaya_gonder: { icon: Send, tone: 'info' },
  onayla: { icon: Check, tone: 'success' },
  geri_gonder: { icon: CornerUpLeft, tone: 'warning' },
  geri_cek: { icon: Undo2, tone: 'neutral' },
}

/** Onay motorunun karar geçmişi (CRUD planı Faz 4): kim, ne zaman, hangi karar, hangi notla. */
export function PaymentDecisionHistory({ decisions }: { decisions: Decision[] }) {
  return (
    <section className="bg-surface rounded-lg border p-3" data-testid="payment-decisions">
      {decisions.length === 0 ? (
        <>
          <h3 className="text-sm font-medium">{t.decisions.title}</h3>
          <p className="text-text-secondary mt-1 text-xs">{t.decisions.empty}</p>
        </>
      ) : (
        <Timeline>
          <TimelineGroup label={t.decisions.title} count={decisions.length} headingLevel={3}>
            {decisions.map((d, index) => {
              const look = DECISION_LOOK[d.decision]
              const Icon = look.icon
              return (
                <TimelineItem
                  key={`${d.at}-${index}`}
                  data-decision={d.decision}
                  icon={<Icon className="size-3.5" />}
                  tone={look.tone}
                  title={t.decisions.kind[d.decision] ?? d.decision}
                  actor={d.user_name ?? undefined}
                  time={formatDateTime(d.at)}
                >
                  {d.note && (
                    <p className="text-text-secondary text-xs whitespace-pre-wrap">{d.note}</p>
                  )}
                </TimelineItem>
              )
            })}
          </TimelineGroup>
        </Timeline>
      )}
    </section>
  )
}

/** Onaycının listeyi notla taslağa geri göndermesi; not zorunlu, alanın altında hata gösterir. */
export function SendBackDialog({
  open,
  firm,
  weekEnd,
  onClose,
}: {
  open: boolean
  firm: string
  weekEnd: string
  onClose: () => void
}) {
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const form = useForm<SendBackValues>({
    resolver: zodResolver(sendBackSchema),
    defaultValues: { note: '' },
  })
  const close = () => {
    form.reset({ note: '' })
    onClose()
  }
  const editor = useRecordEditor({
    form,
    submit: (values) => cashApi.transition(sendBackBody(firm, weekEnd, values)),
    onSaved: (l) => {
      toast({ type: 'success', title: t.transitioned(t.status[l.status] ?? l.status) })
      void invalidate()
      close()
    },
    failedTitle: messages.cash.planning.failed,
  })
  const error = form.formState.errors.note?.message

  return (
    <Dialog open={open} onOpenChange={(next) => !next && editor.requestClose(close)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.sendBackTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {editor.dialog}
          <form
            id={FORM_ID}
            className="flex flex-col gap-1.5"
            noValidate
            onSubmit={(event) => void editor.save(event)}
          >
            <p className="text-text-secondary text-xs">{t.sendBackHint}</p>
            <Label htmlFor={NOTE_ID}>{t.sendBackNote}</Label>
            <Textarea
              id={NOTE_ID}
              {...fieldErrorProps(NOTE_ID, error)}
              {...form.register('note')}
            />
            <FieldError id={NOTE_ID} message={error} />
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => editor.requestClose(close)}>
            {messages.common.cancel}
          </Button>
          <Button type="submit" form={FORM_ID} loading={editor.saving}>
            {t.sendBack}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
